const CORP_LEN = 5;
const HOUSE_LEN = 6;
const CUST_LEN = 2;
const LEGACY_LEN = CORP_LEN + HOUSE_LEN + CUST_LEN;

export type ParsedAccountNumber = {
  corp: string;
  house: string;
  cust: string;
  legacyAccountNumber: string;
  formatted: string;
};

export class AccountNumberError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AccountNumberError";
  }
}

function digitsOnly(input: string): string {
  return input.replace(/\D/g, "");
}

function padSegment(value: string, length: number): string {
  return value.padStart(length, "0").slice(-length);
}

export function formatAccountNumber(legacyAccountNumber: string): string {
  const digits = digitsOnly(legacyAccountNumber);
  if (digits.length !== LEGACY_LEN) {
    throw new AccountNumberError(`Account number must be ${LEGACY_LEN} digits.`);
  }
  const corp = digits.slice(0, CORP_LEN);
  const house = digits.slice(CORP_LEN, CORP_LEN + HOUSE_LEN);
  const cust = digits.slice(CORP_LEN + HOUSE_LEN);
  return `${corp}-${house}-${cust}`;
}

export function fromLegacyInfo(parts: { corp: string; house: string; cust: string }): ParsedAccountNumber {
  const corp = padSegment(parts.corp, CORP_LEN);
  const house = padSegment(parts.house, HOUSE_LEN);
  const cust = padSegment(parts.cust, CUST_LEN);
  const legacyAccountNumber = `${corp}${house}${cust}`;
  return {
    corp,
    house,
    cust,
    legacyAccountNumber,
    formatted: formatAccountNumber(legacyAccountNumber),
  };
}

/**
 * Normalize an Optimum account to 13-digit corp+house+cust. Accepts a full 13-digit value or an
 * 11–12 digit migrated form (left-padded). Names, phones, and other short digit strings throw so
 * callers can take the free-text path instead of searching `0000000000000`.
 */
export function toLegacyAccountNumber(input: string): string {
  const digits = digitsOnly(input);
  if (digits.length === LEGACY_LEN) return digits;
  if (digits.length > LEGACY_LEN || digits.length < 11) {
    throw new AccountNumberError(`Account number must be ${LEGACY_LEN} digits.`);
  }
  // Left-pad 11–12 digit migrated inputs (e.g. demo `44719028815`).
  return digits.padStart(LEGACY_LEN, "0");
}

export function parseAccountNumber(input: string): ParsedAccountNumber {
  const legacyAccountNumber = toLegacyAccountNumber(input);
  const corp = legacyAccountNumber.slice(0, CORP_LEN);
  const house = legacyAccountNumber.slice(CORP_LEN, CORP_LEN + HOUSE_LEN);
  const cust = legacyAccountNumber.slice(CORP_LEN + HOUSE_LEN);
  return {
    corp,
    house,
    cust,
    legacyAccountNumber,
    formatted: formatAccountNumber(legacyAccountNumber),
  };
}

export function normalizeAccountNumber(input: string): string {
  return parseAccountNumber(input).legacyAccountNumber;
}

export function accountSearchKeys(formattedOrLegacy: string): string[] {
  const parsed = parseAccountNumber(formattedOrLegacy);
  const keys = new Set<string>([
    parsed.formatted.toLowerCase(),
    parsed.legacyAccountNumber,
    parsed.corp,
    `${parsed.corp}-${parsed.house}`,
    `${parsed.house}-${parsed.cust}`,
    parsed.corp.replace(/^0+/, "") || parsed.corp,
  ]);
  return [...keys].filter(Boolean);
}

export function accountNumbersMatch(a: string, b: string): boolean {
  try {
    return normalizeAccountNumber(a) === normalizeAccountNumber(b);
  } catch {
    return false;
  }
}
